import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Heart, 
  Shield, 
  Users, 
  Star, 
  MessageCircle, 
  Calendar, 
  CheckCircle,
  BookOpen,
  Target,
  Sparkles,
  Clock,
  Phone,
  Mail,
  AlertTriangle,
  Award,
  Lock,
  Eye,
  UserCheck,
  Coffee,
  Compass,
  Zap,
  Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const EbookPage = () => {
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C3A7A] via-[#1C3A7A] to-[#0f2347]">
      {/* Header */}
      <div className="px-4 py-4 flex justify-between items-center max-w-6xl mx-auto print:hidden">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-[#FAB710] hover:bg-[#e5a60f] text-[#1C3A7A] px-4 py-2 rounded-full text-sm font-semibold transition"
        >
          <Printer className="w-4 h-4" />
          Print / Save PDF
        </button>
      </div>

      {/* Hero Section */}
      <div className="text-center px-4 py-12 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
          <BookOpen className="w-5 h-5 text-[#FAB710]" />
          <span className="text-white/90 text-sm font-medium">Complete Guide • Updated January 2026</span>
        </div>
        
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
          RecoveryMatch
        </h1>
        <h2 className="text-2xl md:text-3xl font-light text-[#FAB710] mb-6">
          The Complete Guide to Dating in Recovery
        </h2>
        
        <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
          Everything you need to know about finding meaningful connections while protecting your sobriety
        </p>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-12">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#FAB710]">16</div>
            <div className="text-white/70 text-xs md:text-sm">Chapters</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#FAB710]">12</div>
            <div className="text-white/70 text-xs md:text-sm">Step Onboarding</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#FAB710]">100%</div>
            <div className="text-white/70 text-xs md:text-sm">Recovery-Focused</div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-2xl font-bold text-[#1C3A7A] mb-6 flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-[#FAB710]" />
            Table of Contents
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Welcome to RecoveryMatch",
              "Why Recovery Dating is Different",
              "Getting Started",
              "The 12-Step Onboarding Journey",
              "Creating Your Profile",
              "Understanding Compatibility Scores",
              "Your Daily Matches",
              "The Art of Guided Messaging",
              "Building Meaningful Connections",
              "Safety First: Protecting Yourself",
              "Date Check-In Safety Feature",
              "Recovery+ & Elite Features",
              "Celebrating Milestones Together",
              "Success Stories",
              "Frequently Asked Questions",
              "Resources for Your Journey"
            ].map((chapter, idx) => (
              <a
                key={idx}
                href={`#chapter-${idx + 1}`}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#1C3A7A]/5 transition group"
              >
                <span className="w-8 h-8 rounded-full bg-[#1C3A7A] text-white text-sm font-bold flex items-center justify-center group-hover:bg-[#FAB710] group-hover:text-[#1C3A7A] transition">
                  {idx + 1}
                </span>
                <span className="text-gray-700 group-hover:text-[#1C3A7A] transition">{chapter}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 1: Welcome */}
      <div id="chapter-1" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 1</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Welcome to RecoveryMatch
          </h2>
          <p className="text-[#1C3A7A]/80 text-lg">
            Everyone in recovery deserves love, and everyone deserves a partner who understands their journey.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#FAB710]" />
            Our Mission
          </h3>
          <p className="text-gray-700 mb-6">
            RecoveryMatch was created with one simple belief: everyone in recovery deserves love, and everyone deserves a partner who understands their journey. We're not just another dating app. We're a community built by people in recovery, for people in recovery.
          </p>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FAB710]" />
            What Makes Us Different
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[
              { icon: Heart, title: "Recovery-First Design", desc: "Every feature is built with your sobriety in mind" },
              { icon: Target, title: "No Swiping", desc: "We focus on intentional connections, not addictive mechanics" },
              { icon: Users, title: "Deep Compatibility", desc: "Our 12-step onboarding ensures meaningful matches" },
              { icon: Shield, title: "Safety Focused", desc: "Robust tools to protect you physically and emotionally" },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-3 p-4 bg-[#1C3A7A]/5 rounded-xl">
                <item.icon className="w-6 h-6 text-[#FAB710] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-[#1C3A7A]">{item.title}</div>
                  <div className="text-gray-600 text-sm">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#FAB710]" />
            Our Philosophy
          </h3>
          <div className="bg-[#1C3A7A] rounded-xl p-6 text-white">
            <ul className="space-y-3">
              {[
                "Your recovery comes first, always",
                "Healthy relationships are built on honesty and shared values",
                "Quality connections matter more than quantity",
                "Safety—physical, emotional, and sobriety-related—is non-negotiable",
                "Everyone deserves a partner who supports their recovery journey"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#FAB710] flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Chapter 2: Why Recovery Dating is Different */}
      <div id="chapter-2" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 2</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Why Recovery Dating is Different
          </h2>
          <p className="text-white/80 text-lg">
            Dating in recovery isn't just "regular dating minus alcohol." It comes with its own set of considerations.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="space-y-4">
            {[
              { 
                title: "Disclosure Decisions", 
                icon: MessageCircle,
                desc: "When do you share about your recovery? First message? First date? When things get serious? There's no perfect answer, but RecoveryMatch removes this pressure—everyone here is in recovery."
              },
              { 
                title: "Navigating an Alcohol-Centric Culture", 
                icon: Coffee,
                desc: "Most 'normal' dates involve drinks. Coffee dates are great, but what about dinner? Events? RecoveryMatch helps you connect with people who understand and embrace sober activities."
              },
              { 
                title: "Different Timelines", 
                icon: Clock,
                desc: "Recovery teaches us to take things slow. Many dating apps encourage fast-paced connections that can be triggering or destabilizing. We prioritize intentional pacing."
              },
              { 
                title: "Protecting Your Sobriety", 
                icon: Shield,
                desc: "A bad date shouldn't threaten your recovery. Our platform includes safeguards to protect your emotional wellbeing and sobriety."
              },
              { 
                title: "Finding Understanding", 
                icon: Heart,
                desc: "Dating someone who doesn't understand recovery can be exhausting. Constantly explaining triggers, meetings, or why you can't 'just have one' takes a toll. Here, understanding is built-in."
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-5 flex gap-4">
                <div className="w-12 h-12 bg-[#1C3A7A] rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-[#FAB710]" />
                </div>
                <div>
                  <h4 className="font-bold text-[#1C3A7A] mb-1">{item.title}</h4>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 3: Getting Started */}
      <div id="chapter-3" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 3</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Getting Started
          </h2>
          <p className="text-[#1C3A7A]/80 text-lg">
            Creating your account and joining the RecoveryMatch community
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#FAB710]" />
            Basic Requirements
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              "Must be 18 years or older (verified during onboarding)",
              "Must have 90+ days of continuous sobriety",
              "Must be committed to their recovery journey",
              "Must agree to community guidelines"
            ].map((req, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-[#1C3A7A]/5 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#1C3A7A] flex-shrink-0" />
                <span className="text-gray-700">{req}</span>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Compass className="w-5 h-5 text-[#FAB710]" />
            The Sign-Up Process
          </h3>
          <div className="space-y-4">
            {[
              { step: 1, title: "Download the App", desc: "Or visit our website" },
              { step: 2, title: "Create Account", desc: "With email or Google sign-in" },
              { step: 3, title: "Verify Your Age", desc: "By entering your birthday" },
              { step: 4, title: "Complete 12-Step Onboarding", desc: "About 10-15 minutes" },
              { step: 5, title: "Add Photos", desc: "Optional but recommended" },
              { step: 6, title: "Start Receiving Matches!", desc: "Your journey begins" },
            ].map((item) => (
              <div key={item.step} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#1C3A7A] text-white font-bold text-xl flex items-center justify-center flex-shrink-0">
                  {item.step}
                </div>
                <div className="flex-1 p-4 bg-gray-50 rounded-xl">
                  <div className="font-semibold text-[#1C3A7A]">{item.title}</div>
                  <div className="text-gray-600 text-sm">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 4: The 12-Step Onboarding Journey */}
      <div id="chapter-4" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 4</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            The 12-Step Onboarding Journey
          </h2>
          <p className="text-white/80 text-lg">
            Our unique onboarding process is themed after the 12-step tradition. Each step helps us understand you better and find truly compatible matches.
          </p>
        </div>

        <div className="space-y-3 mb-8">
          {[
            { step: 1, name: "Honesty", title: "Recovery Basics", desc: "Your clean date, recovery program, meeting attendance" },
            { step: 2, name: "Hope", title: "Recovery Importance", desc: "How important is shared recovery in a partner?" },
            { step: 3, name: "Faith", title: "Spirituality", desc: "Your spiritual approach and practices" },
            { step: 4, name: "Courage", title: "Support System", desc: "Sponsor, home group, community involvement" },
            { step: 5, name: "Integrity", title: "Core Values", desc: "Your top 5 values for compatibility" },
            { step: 6, name: "Willingness", title: "Relationship Intent", desc: "What are you looking for?" },
            { step: 7, name: "Humility", title: "Boundaries & Pacing", desc: "Communication and conflict styles" },
            { step: 8, name: "Love", title: "Lifestyle", desc: "Activities, social style, routines" },
            { step: 9, name: "Justice", title: "Substance Preferences", desc: "Transparency about substance-adjacent topics" },
            { step: 10, name: "Perseverance", title: "Match Preferences", desc: "Age range, distance, gender preferences" },
            { step: 11, name: "Awareness", title: "Profile Prompts", desc: "Showcase your personality" },
            { step: 12, name: "Service", title: "Photos", desc: "6 photo slots to showcase yourself" },
          ].map((item) => (
            <div key={item.step} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-[#1C3A7A] p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-[#FAB710] rounded-full flex items-center justify-center text-[#1C3A7A] font-bold">
                  {item.step}
                </div>
                <div>
                  <div className="text-[#FAB710] text-sm font-medium">{item.name}</div>
                  <div className="text-white font-bold">{item.title}</div>
                </div>
              </div>
              <div className="p-4 text-gray-600">
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chapter 5: Creating Your Profile */}
      <div id="chapter-5" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 5</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Creating Your Profile
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">Profile Elements</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[
              { title: "Basic Info", desc: "Name, age, location" },
              { title: "Photos", desc: "Up to 6 images" },
              { title: "Prompts", desc: "2-3 conversation starters" },
              { title: "Recovery Details", desc: "Program, clean time, terminology" },
            ].map((item, idx) => (
              <div key={idx} className="p-4 bg-[#1C3A7A]/5 rounded-xl">
                <div className="font-bold text-[#1C3A7A]">{item.title}</div>
                <div className="text-gray-600 text-sm">{item.desc}</div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">Profile Visibility Settings</h3>
          <ul className="space-y-2">
            {[
              "Show/hide recovery milestone badges",
              "Set discovery preferences (age, distance, gender)",
              "Pause your profile when needed",
              "Control what appears to free vs. premium users"
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-gray-700">
                <Eye className="w-4 h-4 text-[#FAB710]" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Chapter 6: Understanding Compatibility Scores */}
      <div id="chapter-6" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 6</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Understanding Compatibility Scores
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-6">How Scores Are Calculated</h3>
          
          <div className="space-y-4 mb-8">
            {[
              { pct: "25%", title: "Recovery Alignment", desc: "Similar clean time, compatible programs, shared recovery views" },
              { pct: "25%", title: "Core Values", desc: "Matching values, complementary life priorities" },
              { pct: "20%", title: "Relationship Intentions", desc: "Same goals, compatible timelines, aligned views on marriage" },
              { pct: "15%", title: "Lifestyle Compatibility", desc: "Shared activities, compatible social styles, similar routines" },
              { pct: "15%", title: "Boundaries & Pacing", desc: "Compatible communication, aligned pacing, similar conflict approaches" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#1C3A7A] rounded-xl flex items-center justify-center text-[#FAB710] font-bold text-lg">
                  {item.pct}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[#1C3A7A]">{item.title}</div>
                  <div className="text-gray-600 text-sm">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">What the Scores Mean</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { range: "90-100%", label: "Exceptional", desc: "Very rare, high compatibility" },
              { range: "75-89%", label: "Strong", desc: "Worth exploring" },
              { range: "60-74%", label: "Good", desc: "Some differences to discuss" },
              { range: "< 60%", label: "Limited", desc: "Proceed thoughtfully" },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-[#1C3A7A] rounded-full mx-auto mb-2 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#FAB710]" />
                </div>
                <div className="font-bold text-[#1C3A7A] text-lg">{item.range}</div>
                <div className="font-medium text-gray-700">{item.label}</div>
                <div className="text-gray-500 text-xs">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 7: Your Daily Matches */}
      <div id="chapter-7" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 7</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Your Daily Matches
          </h2>
          <p className="text-[#1C3A7A]/80 text-lg">
            Instead of endless swiping, you receive a curated selection of matches each day
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { tier: "Free", matches: "3", desc: "matches/day" },
              { tier: "Recovery+", matches: "5", desc: "matches/day" },
              { tier: "Recovery Elite", matches: "7", desc: "matches/day" },
            ].map((item, idx) => (
              <div key={idx} className={`rounded-xl p-6 text-center ${idx === 2 ? 'bg-[#FAB710] text-[#1C3A7A]' : idx === 1 ? 'bg-[#1C3A7A] text-white' : 'bg-gray-100 text-gray-700'}`}>
                <div className="text-4xl font-bold mb-1">{item.matches}</div>
                <div className="opacity-80 text-sm">{item.desc}</div>
                <div className="font-semibold mt-2">{item.tier}</div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">Making Decisions</h3>
          <div className="space-y-3 mb-6">
            {[
              { action: "Express Interest", desc: "Shows you'd like to connect", icon: Heart },
              { action: "Not Aligned", desc: "Pass on this match (they won't know)", icon: Target },
              { action: "View Full Profile", desc: "See all photos, prompts, and details", icon: Eye },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <item.icon className="w-5 h-5 text-[#FAB710]" />
                <span className="font-medium text-[#1C3A7A]">{item.action}:</span>
                <span className="text-gray-600">{item.desc}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#1C3A7A] rounded-xl p-6 text-white">
            <h4 className="font-bold text-lg mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FAB710]" />
              Mutual Matches
            </h4>
            <p className="text-white/80">
              When both people express interest, it's a mutual match! You can then start a guided conversation, view additional profile details, and see compatibility breakdown.
            </p>
          </div>
        </div>
      </div>

      {/* Chapter 8: The Art of Guided Messaging */}
      <div id="chapter-8" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 8</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            The Art of Guided Messaging
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">Why Guided Messaging?</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              "Reduces pressure of 'what do I say?'",
              "Encourages meaningful conversation from the start",
              "Helps identify compatibility early",
              "Protects against inappropriate openers"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-[#1C3A7A]/5 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#1C3A7A]" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4">How It Works</h3>
          <div className="space-y-4">
            {[
              { phase: "Phase 1", title: "Guided Prompts", desc: "Choose from provided conversation starters related to recovery, values, and interests." },
              { phase: "Phase 2", title: "Deeper Questions", desc: "After initial exchange, unlock more personal prompts about relationships and future goals." },
              { phase: "Phase 3", title: "Free-Form", desc: "Once connection is established, open conversation unlocks for natural communication." },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="bg-[#1C3A7A] text-[#FAB710] font-bold px-4 py-2 rounded-lg text-sm whitespace-nowrap h-fit">
                  {item.phase}
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-4">
                  <div className="font-bold text-[#1C3A7A]">{item.title}</div>
                  <div className="text-gray-600 text-sm">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 9: Building Meaningful Connections */}
      <div id="chapter-9" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 9</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Building Meaningful Connections
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Coffee className="w-5 h-5 text-[#FAB710]" />
            Sober First Date Ideas
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              "Coffee or tea shops",
              "Hiking or walking trails",
              "Museums or galleries",
              "Cooking classes",
              "Farmers markets",
              "Mini golf or bowling",
              "Recovery-friendly restaurants",
              "Volunteer activities"
            ].map((item, idx) => (
              <div key={idx} className="p-3 bg-[#1C3A7A]/5 rounded-lg text-center text-gray-700 text-sm">
                {item}
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FAB710]" />
            First Date Guidelines
          </h3>
          <div className="bg-[#1C3A7A]/5 rounded-xl p-6 mb-8">
            <ul className="space-y-2">
              {[
                "Meet in public places",
                "Keep first dates short (1-2 hours)",
                "Have your own transportation",
                "Tell someone where you'll be",
                "Trust your instincts"
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 text-[#1C3A7A]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Chapter 10: Safety First */}
      <div id="chapter-10" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 10</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Safety First: Protecting Yourself
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-lg font-bold text-[#1C3A7A] mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#FAB710]" />
                Digital Safety
              </h3>
              <ul className="space-y-2">
                {[
                  "Keep conversations on-app initially",
                  "Don't share phone number/address early",
                  "Be cautious about social media connections",
                  "Never send money to matches",
                  "Report suspicious behavior immediately"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                    <Shield className="w-4 h-4 text-[#1C3A7A] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1C3A7A] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#FAB710]" />
                Red Flags
              </h3>
              <ul className="space-y-2">
                {[
                  "Love bombing (excessive attention too fast)",
                  "Isolation attempts",
                  "Criticism of your recovery program",
                  "Pressure around substances",
                  "Guilt trips or manipulation"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                    <AlertTriangle className="w-4 h-4 text-[#FAB710] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FAB710]" />
            RecoveryMatch Safety Features
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: UserCheck, title: "Verification", desc: "AI-powered photo verification" },
              { icon: AlertTriangle, title: "Reporting", desc: "Report uncomfortable behavior" },
              { icon: Lock, title: "Blocking", desc: "Block users instantly" },
              { icon: Users, title: "Moderation", desc: "Reviews reports within 24 hours" },
              { icon: Eye, title: "Privacy", desc: "Location never shared precisely" },
              { icon: Shield, title: "Encryption", desc: "All data encrypted" },
            ].map((item, idx) => (
              <div key={idx} className="p-4 bg-[#1C3A7A]/5 rounded-xl text-center">
                <item.icon className="w-8 h-8 text-[#1C3A7A] mx-auto mb-2" />
                <div className="font-bold text-[#1C3A7A] text-sm">{item.title}</div>
                <div className="text-gray-600 text-xs">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 11: Date Check-In */}
      <div id="chapter-11" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 11</span>
            <span className="bg-[#1C3A7A] text-white px-3 py-1 rounded-full text-sm font-bold">NEW</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Date Check-In Safety Feature
          </h2>
          <p className="text-[#1C3A7A]/80 text-lg">
            A safety feature that checks on you during dates and can alert your emergency contact if needed
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { response: "I'm Safe!", desc: "Great! Enjoy your date", color: "bg-[#1C3A7A]" },
              { response: "Date Ended", desc: "Thanks for letting us know", color: "bg-[#1C3A7A]/70" },
              { response: "I Need Help!", desc: "Immediately alerts your emergency contact", color: "bg-[#FAB710]" },
            ].map((item, idx) => (
              <div key={idx} className={`${item.color} ${idx === 2 ? 'text-[#1C3A7A]' : 'text-white'} rounded-xl p-4 text-center`}>
                <div className="font-bold mb-1">{item.response}</div>
                <div className={idx === 2 ? 'text-[#1C3A7A]/70' : 'text-white/80'} style={{fontSize: '0.875rem'}}>{item.desc}</div>
              </div>
            ))}
          </div>

          <div className="bg-[#FAB710]/20 border-l-4 border-[#FAB710] p-4 rounded-r-xl">
            <h4 className="font-bold text-[#1C3A7A] mb-2">If You Don't Respond</h4>
            <p className="text-gray-700 text-sm">
              If you don't respond within 30 minutes of the check-in time, we automatically send an alert to your emergency contact with your date's name, location, and scheduled time.
            </p>
          </div>
        </div>
      </div>

      {/* Chapter 12: Recovery+ & Elite Features */}
      <div id="chapter-12" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 12</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Recovery+ & Elite Features
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Free Tier */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gray-100 p-6 text-center">
              <div className="text-2xl font-bold text-gray-700">Free</div>
              <div className="text-gray-500">$0/month</div>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {["3 daily matches", "Basic messaging", "Profile creation", "Standard filters"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#1C3A7A]" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-gray-500 italic">
                Best for: Those exploring the app or dating casually
              </p>
            </div>
          </div>

          {/* Recovery+ */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#1C3A7A]">
            <div className="bg-[#1C3A7A] p-6 text-center">
              <div className="text-2xl font-bold text-white">Recovery+</div>
              <div className="text-white/80">$9.99/month</div>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {["5 daily matches", "See who's interested", "Read receipts", "Second Chance feature", "Advanced filters"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#1C3A7A]" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-gray-500 italic">
                Best for: Active daters ready for more connections
              </p>
            </div>
          </div>

          {/* Recovery Elite */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#FAB710]">
            <div className="bg-[#FAB710] p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Award className="w-5 h-5 text-[#1C3A7A]" />
                <span className="text-2xl font-bold text-[#1C3A7A]">Recovery Elite</span>
              </div>
              <div className="text-[#1C3A7A]/80">$19.99/month</div>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {["7 daily matches", "Video calls in-app", "Priority visibility", "Verification badge", "Weekly profile boost"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#FAB710]" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-gray-500 italic">
                Best for: Serious about finding a long-term partner
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Chapter 13: Celebrating Milestones */}
      <div id="chapter-13" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 13</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Celebrating Milestones Together
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#FAB710]" />
            Recovery Milestones
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {["9 Months", "1 Year", "18 Months", "2+ Years"].map((milestone, idx) => (
              <div key={idx} className="bg-[#1C3A7A]/5 rounded-xl p-4 text-center">
                <Award className="w-8 h-8 text-[#FAB710] mx-auto mb-2" />
                <div className="font-bold text-[#1C3A7A]">{milestone}</div>
              </div>
            ))}
          </div>

          <div className="bg-[#1C3A7A] rounded-xl p-6 text-white">
            <h4 className="font-bold flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-[#FAB710]" />
              "Day One" Badge
            </h4>
            <p className="text-white/80">
              Early supporters who joined during our beta period receive a special "Day One" badge—a permanent thank you for believing in us from the start.
            </p>
          </div>
        </div>
      </div>

      {/* Chapter 14: Success Stories */}
      <div id="chapter-14" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 14</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Success Stories
          </h2>
        </div>

        <div className="space-y-6 mb-8">
          {[
            { 
              names: "Sarah & Michael",
              location: "Philadelphia",
              quote: "We matched on our first day using RecoveryMatch. Both NA members, both from Philly, both 2+ years clean. The compatibility score was 94%. Three years later, we're married. This app gave us something other apps couldn't: understanding."
            },
            { 
              names: "Jennifer & David",
              location: "New York",
              quote: "After my divorce, I didn't think I'd find someone who understood why I couldn't just 'have a glass of wine at dinner.' David got it immediately. The guided messaging helped us build a real connection before meeting. We've been together 18 months."
            },
            { 
              names: "Amanda & Chris",
              location: "Chicago",
              quote: "The 12-step onboarding felt intense at first, but it meant that when Chris and I matched, we already knew we were aligned on what matters. We're taking things slow, just like recovery taught us, and it's working."
            },
          ].map((story, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 md:p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-6 h-6 text-[#FAB710]" />
                <div>
                  <div className="font-bold text-[#1C3A7A]">{story.names}</div>
                  <div className="text-gray-500 text-sm">{story.location}</div>
                </div>
              </div>
              <p className="text-gray-700 italic">"{story.quote}"</p>
            </div>
          ))}
        </div>
      </div>

      {/* Chapter 15: FAQ */}
      <div id="chapter-15" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#FAB710] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-white/20 text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 15</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="space-y-4">
            {[
              { q: "How is RecoveryMatch different from other dating apps?", a: "We're built exclusively for people in recovery, with features designed to protect sobriety and foster genuine connections." },
              { q: "Is my recovery status visible to everyone?", a: "Only to other members. Your profile is never publicly searchable." },
              { q: "What recovery programs are welcome?", a: "All of them—AA, NA, CA, SMART Recovery, Celebrate Recovery, LifeRing, therapy-based recovery, and more." },
              { q: "Why do I need 90 days of sobriety?", a: "Early recovery requires focus on yourself. We want members stable enough for healthy dating." },
              { q: "Can I pause my account?", a: "Yes, you can pause and unpause anytime from settings." },
              { q: "How do I report someone?", a: "Tap the flag icon on their profile or in chat. Our team reviews within 24 hours." },
              { q: "Is my data secure?", a: "Yes. We use encryption, never sell data, and allow full deletion." },
            ].map((faq, idx) => (
              <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                <div className="font-bold text-[#1C3A7A] mb-2 flex items-start gap-2">
                  <span className="text-[#FAB710]">Q:</span>
                  {faq.q}
                </div>
                <div className="text-gray-600 pl-6">
                  <span className="font-medium text-[#1C3A7A]">A:</span> {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chapter 16: Resources */}
      <div id="chapter-16" className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-[#1C3A7A] rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FAB710] text-[#1C3A7A] px-3 py-1 rounded-full text-sm font-bold">Chapter 16</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Resources for Your Journey
          </h2>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mb-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#FAB710]" />
                Crisis Resources
              </h3>
              <div className="space-y-3">
                {[
                  { name: "National Suicide Prevention Lifeline", number: "988" },
                  { name: "SAMHSA National Helpline", number: "1-800-662-4357" },
                  { name: "Crisis Text Line", number: "Text HOME to 741741" },
                ].map((resource, idx) => (
                  <div key={idx} className="p-3 bg-[#1C3A7A]/5 rounded-lg">
                    <div className="font-medium text-gray-700">{resource.name}</div>
                    <div className="text-[#1C3A7A] font-bold">{resource.number}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FAB710]" />
                Recovery Resources
              </h3>
              <div className="space-y-2">
                {[
                  { name: "Alcoholics Anonymous", url: "aa.org" },
                  { name: "Narcotics Anonymous", url: "na.org" },
                  { name: "SMART Recovery", url: "smartrecovery.org" },
                  { name: "Celebrate Recovery", url: "celebraterecovery.com" },
                  { name: "In The Rooms", url: "intherooms.com" },
                ].map((resource, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-[#1C3A7A]/5 rounded-lg">
                    <span className="text-gray-700">{resource.name}</span>
                    <span className="text-[#1C3A7A] text-sm font-medium">{resource.url}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#FAB710]" />
              RecoveryMatch Support
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { label: "General Support", email: "support@recoverymatch.com" },
                { label: "Privacy Concerns", email: "support@recoverymatch.com" },
                { label: "Legal Inquiries", email: "support@recoverymatch.com" },
              ].map((contact, idx) => (
                <div key={idx} className="p-3 bg-[#1C3A7A]/5 rounded-lg text-center">
                  <div className="text-gray-500 text-sm">{contact.label}</div>
                  <div className="text-[#1C3A7A] font-medium text-sm">{contact.email}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Final Thoughts */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-[#1C3A7A] rounded-2xl p-8 md:p-12 shadow-xl text-center">
          <Heart className="w-12 h-12 text-[#FAB710] mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Final Thoughts
          </h2>
          <p className="text-white/80 text-lg mb-6 max-w-2xl mx-auto">
            Dating in recovery is a journey, just like recovery itself. Some days will be easier than others. Some connections won't work out. But with patience, honesty, and the right tools, meaningful love is absolutely possible.
          </p>
          <p className="text-white/70 mb-8">
            RecoveryMatch is here to support you every step of the way. We believe in you. We believe in recovery. And we believe that everyone deserves a partner who understands and supports their journey.
          </p>
          <div className="text-[#FAB710] text-xl font-medium italic mb-8">
            "One day at a time. One connection at a time."
          </div>
          <div className="text-white text-2xl font-bold">
            Welcome to RecoveryMatch
          </div>
          <div className="text-white/60 text-sm mt-4">
            Where Recovery Meets Connection
          </div>
          
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center print:hidden">
            <Button
              onClick={() => navigate("/register")}
              className="bg-[#FAB710] hover:bg-[#e5a60f] text-[#1C3A7A] px-8 py-3 rounded-full text-lg font-semibold"
            >
              Join Our Community
            </Button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-full text-lg font-semibold transition"
            >
              <Printer className="w-5 h-5" />
              Print / Save PDF
            </button>
          </div>
        </div>

        <div className="text-center mt-8 text-white/60 text-sm">
          ©2026 RecoveryMatch. All rights reserved.
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EbookPage;
